import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import U2000BasicDialog from "../../../u2000Core/components/dialog/U2000BasicDialog";
import U2000KeyboardKeyHint from "../../../u2000Core/components/common/U2000KeyboardKeyHint";
import U2000AutoCompleteInput from "../../../u2000Core/components/formControls/autoComplete/U2000AutoCompleteInput";
import U2000TagAutocomplete from "../../../u2000Core/components/tables/commons/search/U2000TagAutocomplete";
import type { U2000CriteriaTag } from "../../../u2000Core/components/tables/commons/search/u2000CriteriaTags";
import { useToast } from "../../../u2000Core/context/toast/U2000ToastContext";
import u2000FormState, {
  U2000FormStateEnumAction,
  U2000FormStateEnumEditMode,
  U2000FormStateEnumState,
  type U2000FormState,
} from "../../../u2000Core/reducer/formReducer/U2000FormState";
import { u2000IntFormState, useU2000FormStateHandlers } from "../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { u2000NormalizeAutoCompleteSearchResult } from "../../../u2000Core/utils/u2000NormalizeAutoCompleteSearchResult";
import {
  u3129BuildFindTasksForProjectCacheKey,
  u3129InvalidateFindTasksForProjectCache,
  useU3129FindProjectHandlers,
  useU3129FindSubprojectHandlers,
  useU3129FindTasksForProjectHandlers,
} from "../../U3129UseFindProjectSubprojectHandlers";
import type {
  U3129FindProjectItem,
  U3129FindSubprojectItem,
  U3129HonorariumItem,
  U3129HonorariumTasksByProject,
  U3129TaskItem,
} from "../../U3129Service";
import { NS } from "../../U3129";
import {
  buildHonorariumLinesFromProjectAndTasks,
  u3129HonorariumLineProjectSubprojectTaskKey,
  u3129HonorariumLineProjectSubprojectTaskKeyFromParts,
} from "../../tools/u3129BuildHonorariumLinesFromTasks";
import { formatU3129TaskLabel, u3129SortTasksByDisplayLabel } from "../../tools/u3129HonorariumTaskLabel";
import {
  U3129_HONORARIUM_MULTI_PROJECT_MAX,
  u3129BuildHonorariumLinesForMultiProjects,
  u3129BuildTasksForCacheByProjectId,
  u3129CountNewHonorariumPairsMulti,
  u3129FindProjectChipLabel,
  u3129FindProjectChipTitle,
  u3129FindProjectChipSearchField,
  u3129MultiTaskRowFullyOnSheet,
  u3129ProjectRejectsMultiChipLine,
} from "../../tools/u3129HonorariumMultiProject";

export type U3129AddHonorariumLinesDialogProps = {
  open: boolean;
  onClose: () => void;
  /** Lignes honoraires déjà sur la feuille (évite d’ajouter le même projet / sous-projet / tâche). */
  existingHonoraria: U3129HonorariumItem[];
  /** Lignes créées + cache tâches par projet (findTasksForProject). */
  onConfirm: (rows: U3129HonorariumItem[], tasksForCacheByProjectId: Record<string, U3129TaskItem[]>) => void;
  admIncludeSubProjectEnabled: boolean;
};

type SelectionMode = "single" | "multi";

type HonorariumDialogTaskFocusColumn = "common" | "catalog";

type HonorariumDialogTaskFocus = {
  column: HonorariumDialogTaskFocusColumn;
  taskId: string;
};

function u3129IsHonorariumDialogTaskNavCheckbox(active: Element | null): active is HTMLInputElement {
  return (
    active instanceof HTMLInputElement &&
    active.type === "checkbox" &&
    (active.dataset.u3129HonTaskNav === "common" || active.dataset.u3129HonTaskNav === "catalog")
  );
}

type SubprojectDraft = { subproject: U3129FindSubprojectItem | null };

const subprojectFormInitial = (): U2000FormState<SubprojectDraft> => ({
  ...u2000IntFormState,
  current: { subproject: null },
  old: { subproject: null },
  state: U2000FormStateEnumState.EDIT,
  editMode: U2000FormStateEnumEditMode.EDIT,
  touched: {},
  errors: {},
  otherErrors: {},
  isSubmitted: false,
  customAttributes: {},
});

function projectNeedsSubproject(project: U3129FindProjectItem | null, admIncludeSubProjectEnabled: boolean): boolean {
  if (!project || !admIncludeSubProjectEnabled) return false;
  const v = project.includeSubProjet;
  return v === true || (typeof v === "string" && v === "true");
}

/** Hauteur fixe du corps (au-dessus des boutons) — identique avec ou sans projet / tâches. */
const U3129_ADD_HON_DIALOG_BODY_HEIGHT_CLASS = "h-[640px]";

function projectsFromTags(tags: U2000CriteriaTag[]): U3129FindProjectItem[] {
  const out: U3129FindProjectItem[] = [];
  const seen = new Set<string>();
  for (const tag of tags) {
    const project = tag.meta as U3129FindProjectItem | undefined;
    if (!project?.id?.trim()) continue;
    const id = project.id.trim();
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(project);
  }
  return out;
}

function tagsFromProjects(projects: U3129FindProjectItem[]): U2000CriteriaTag[] {
  return projects.map((p) => ({
    value: p.id,
    label: u3129FindProjectChipLabel(p),
    meta: p,
  }));
}

const U3129AddHonorariumLinesDialog: React.FC<U3129AddHonorariumLinesDialogProps> = ({
  open,
  onClose,
  existingHonoraria,
  onConfirm,
  admIncludeSubProjectEnabled,
}) => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const findProjectHandlers = useU3129FindProjectHandlers();
  const {
    fetchTasks,
    fetchTasksNotAssigned,
    assignTasksToProjectScope,
    fetchCommonTasksForProjects,
    fetchActiveTasks,
    assignTasksToSelectedProjects,
  } = useU3129FindTasksForProjectHandlers();

  const [selectionMode, setSelectionMode] = useState<SelectionMode>("multi");
  const [multiProjects, setMultiProjects] = useState<U3129FindProjectItem[]>([]);
  const [projectTags, setProjectTags] = useState<U2000CriteriaTag[]>([]);
  const [selectedProject, setSelectedProject] = useState<U3129FindProjectItem | null>(null);

  const [subFormState, subFormDispatch] = useReducer(u2000FormState, subprojectFormInitial());
  const subprojectFormHandlers = useU2000FormStateHandlers<SubprojectDraft>({
    formState: subFormState,
    formStateDispatch: subFormDispatch,
    businessHandlers: {
      newRecord: () => ({ subproject: null }),
    },
  });

  const getProjectIdForSub = useCallback(() => selectedProject?.id ?? null, [selectedProject?.id]);
  const findSubprojectHandlers = useU3129FindSubprojectHandlers({ getProjectId: getProjectIdForSub });

  const [commonTaskList, setCommonTaskList] = useState<U3129TaskItem[]>([]);
  const [byProjectTasks, setByProjectTasks] = useState<U3129HonorariumTasksByProject[]>([]);
  const [activeCatalogList, setActiveCatalogList] = useState<U3129TaskItem[]>([]);
  const [extraLeftTaskIds, setExtraLeftTaskIds] = useState<Set<string>>(new Set());

  const [taskList, setTaskList] = useState<U3129TaskItem[]>([]);
  const [catalogTaskList, setCatalogTaskList] = useState<U3129TaskItem[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [confirmAssigning, setConfirmAssigning] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(new Set());
  const [pendingAssignTaskIds, setPendingAssignTaskIds] = useState<Set<string>>(new Set());
  const [focusedTaskNav, setFocusedTaskNav] = useState<HonorariumDialogTaskFocus | null>(null);
  const focusedTaskNavRef = useRef(focusedTaskNav);
  focusedTaskNavRef.current = focusedTaskNav;
  const taskRowRefs = useRef<Record<number, HTMLLIElement | null>>({});
  const catalogTaskRowRefs = useRef<Record<number, HTMLLIElement | null>>({});

  const needsSubproject = useMemo(() => projectNeedsSubproject(selectedProject, admIncludeSubProjectEnabled), [selectedProject, admIncludeSubProjectEnabled]);
  const selectedSubproject = subprojectFormHandlers.getData()?.subproject ?? null;

  const existingHonorariumKeys = useMemo(
    () => new Set((existingHonoraria ?? []).map((r) => u3129HonorariumLineProjectSubprojectTaskKey(r))),
    [existingHonoraria],
  );

  const multiLeftTaskList = useMemo(() => {
    const byId = new Map<string, U3129TaskItem>();
    for (const tk of commonTaskList) {
      byId.set(tk.id, tk);
    }
    for (const id of extraLeftTaskIds) {
      if (!byId.has(id)) {
        const hit = activeCatalogList.find((tk) => tk.id === id);
        if (hit) {
          byId.set(id, hit);
        }
      }
    }
    return u3129SortTasksByDisplayLabel([...byId.values()], i18n.language);
  }, [commonTaskList, extraLeftTaskIds, activeCatalogList, i18n.language]);

  const resetDialog = useCallback(() => {
    setSelectionMode("multi");
    setMultiProjects([]);
    setProjectTags([]);
    setSelectedProject(null);
    setCommonTaskList([]);
    setByProjectTasks([]);
    setActiveCatalogList([]);
    setExtraLeftTaskIds(new Set());
    setTaskList([]);
    setCatalogTaskList([]);
    setTasksLoading(false);
    setCatalogLoading(false);
    setConfirmAssigning(false);
    setSelectedTaskIds(new Set());
    setPendingAssignTaskIds(new Set());
    setFocusedTaskNav(null);
    subFormDispatch({ type: U2000FormStateEnumAction.SETDATA, data: { row: { subproject: null } } });
  }, [subFormDispatch]);

  const handleCancel = useCallback(() => {
    resetDialog();
    onClose();
  }, [resetDialog, onClose]);

  useEffect(() => {
    if (!open) {
      resetDialog();
    }
  }, [open, resetDialog]);

  const clearTaskPickState = useCallback(() => {
    setSelectedTaskIds(new Set());
    setPendingAssignTaskIds(new Set());
    setExtraLeftTaskIds(new Set());
    setFocusedTaskNav(null);
  }, []);

  const tryAddMultiProject = useCallback(
    (project: U3129FindProjectItem) => {
      if (u3129ProjectRejectsMultiChipLine(project, admIncludeSubProjectEnabled)) {
        if (multiProjects.length === 0) {
          setSelectionMode("single");
          setSelectedProject(project);
          subprojectFormHandlers.setData({ subproject: null });
          return;
        }
        toast.warning(
          t("dlgAddHonorariumLinesSubProjectNotOnMultiLine", {
            ns: NS,
            defaultValue: "Les projets avec sous-projets ne peuvent pas être ajoutés à la ligne multi-projets.",
          }),
        );
        return;
      }
      if (multiProjects.some((p) => p.id === project.id)) {
        toast.info(
          t("dlgAddHonorariumLinesProjectAlreadySelected", {
            ns: NS,
            defaultValue: "Ce projet est déjà sur la ligne.",
          }),
        );
        return;
      }
      if (multiProjects.length >= U3129_HONORARIUM_MULTI_PROJECT_MAX) {
        toast.warning(
          t("dlgAddHonorariumLinesMaxProjects", {
            ns: NS,
            defaultValue: "Maximum {{max}} projets sur la ligne.",
            max: U3129_HONORARIUM_MULTI_PROJECT_MAX,
          }),
        );
        return;
      }
      const next = [...multiProjects, project];
      setMultiProjects(next);
      setProjectTags(tagsFromProjects(next));
    },
    [admIncludeSubProjectEnabled, multiProjects, subprojectFormHandlers, t, toast],
  );

  const handleProjectTagsChange = useCallback(
    (newTags: U2000CriteriaTag[]) => {
      if (newTags.length < projectTags.length) {
        setProjectTags(newTags);
        setMultiProjects(projectsFromTags(newTags));
        return;
      }
      const last = newTags[newTags.length - 1];
      const project = last?.meta as U3129FindProjectItem | undefined;
      if (!project?.id?.trim()) {
        return;
      }
      if (u3129ProjectRejectsMultiChipLine(project, admIncludeSubProjectEnabled)) {
        if (multiProjects.length === 0) {
          setSelectionMode("single");
          setSelectedProject(project);
          subprojectFormHandlers.setData({ subproject: null });
          return;
        }
        toast.warning(
          t("dlgAddHonorariumLinesSubProjectNotOnMultiLine", {
            ns: NS,
            defaultValue: "Les projets avec sous-projets ne peuvent pas être ajoutés à la ligne multi-projets.",
          }),
        );
        return;
      }
      if (multiProjects.some((p) => p.id === project.id)) {
        toast.info(
          t("dlgAddHonorariumLinesProjectAlreadySelected", {
            ns: NS,
            defaultValue: "Ce projet est déjà sur la ligne.",
          }),
        );
        return;
      }
      if (multiProjects.length >= U3129_HONORARIUM_MULTI_PROJECT_MAX) {
        toast.warning(
          t("dlgAddHonorariumLinesMaxProjects", {
            ns: NS,
            defaultValue: "Maximum {{max}} projets sur la ligne.",
            max: U3129_HONORARIUM_MULTI_PROJECT_MAX,
          }),
        );
        return;
      }
      const next = [...multiProjects, project];
      setMultiProjects(next);
      setProjectTags(tagsFromProjects(next));
    },
    [projectTags.length, multiProjects, admIncludeSubProjectEnabled, subprojectFormHandlers, t, toast],
  );

  const chipProjectSearch = useCallback(
    async (criterias: { fieldName: string; value: string }[]) => {
      const query = String(criterias[0]?.value ?? "").trim();
      const field = u3129FindProjectChipSearchField(query);
      const pageSize = criterias.find((c) => c.fieldName === "pageSize")?.value ?? "15";
      const raw = await findProjectHandlers.search([
        { fieldName: field, value: query },
        { fieldName: "pageSize", value: String(pageSize) },
      ]);
      return raw.results;
    },
    [findProjectHandlers],
  );

  useEffect(() => {
    if (!open || selectionMode !== "multi" || multiProjects.length === 0) {
      setCommonTaskList([]);
      setByProjectTasks([]);
      setActiveCatalogList([]);
      setSelectedTaskIds(new Set());
      setPendingAssignTaskIds(new Set());
      setExtraLeftTaskIds(new Set());
      return;
    }
    let cancelled = false;
    const projectIds = multiProjects.map((p) => p.id.trim());
    setTasksLoading(true);
    setCatalogLoading(true);
    void Promise.all([fetchCommonTasksForProjects({ projectIds }), fetchActiveTasks({})])
      .then(([commonResp, active]) => {
        if (cancelled) return;
        if (commonResp) {
          setCommonTaskList(u3129SortTasksByDisplayLabel(commonResp.results, i18n.language));
          setByProjectTasks(commonResp.byProject);
        } else {
          setCommonTaskList([]);
          setByProjectTasks([]);
        }
        setActiveCatalogList(u3129SortTasksByDisplayLabel(active, i18n.language));
        setSelectedTaskIds(new Set());
        setPendingAssignTaskIds(new Set());
        setExtraLeftTaskIds(new Set());
      })
      .finally(() => {
        if (!cancelled) {
          setTasksLoading(false);
          setCatalogLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, selectionMode, multiProjects, fetchCommonTasksForProjects, fetchActiveTasks, i18n.language]);

  const canLoadTasksSubproject = useMemo(() => {
    const pid = selectedProject?.id?.trim();
    if (!pid) return false;
    if (needsSubproject) {
      return !!selectedSubproject?.id?.trim();
    }
    return true;
  }, [selectedProject?.id, needsSubproject, selectedSubproject?.id]);

  useEffect(() => {
    if (!open || selectionMode !== "single" || !selectedProject || !canLoadTasksSubproject) {
      if (selectionMode === "single" && selectedProject) {
        setTaskList([]);
        setCatalogTaskList([]);
        setSelectedTaskIds(new Set());
        setPendingAssignTaskIds(new Set());
      }
      return;
    }
    let cancelled = false;
    const scopeBody = {
      projectId: selectedProject!.id,
      subprojectId: needsSubproject ? selectedSubproject!.id : null,
    };
    setTasksLoading(true);
    setCatalogLoading(true);
    void Promise.all([fetchTasks(scopeBody), fetchTasksNotAssigned(scopeBody)])
      .then(([assigned, catalog]) => {
        if (!cancelled) {
          setTaskList(u3129SortTasksByDisplayLabel(assigned, i18n.language));
          setCatalogTaskList(u3129SortTasksByDisplayLabel(catalog, i18n.language));
          setSelectedTaskIds(new Set());
          setPendingAssignTaskIds(new Set());
        }
      })
      .finally(() => {
        if (!cancelled) {
          setTasksLoading(false);
          setCatalogLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    open,
    selectionMode,
    selectedProject,
    canLoadTasksSubproject,
    fetchTasks,
    fetchTasksNotAssigned,
    selectedProject,
    needsSubproject,
    selectedSubproject,
    i18n.language,
  ]);

  const onSearchSubproject = useCallback(
    async (criterias: { fieldName: string; value: unknown }[]) => {
      const raw = await findSubprojectHandlers.search(criterias);
      const normalized = u2000NormalizeAutoCompleteSearchResult(raw);
      return normalized.results.map((r: U3129FindSubprojectItem) => ({ value: r.id, ...r }));
    },
    [findSubprojectHandlers],
  );

  const extractSubproject = useCallback((result: U3129FindSubprojectItem | null): { fieldName: string; value: unknown }[] => {
    if (result) {
      return [{ fieldName: "subproject", value: result }];
    }
    return [{ fieldName: "subproject", value: null }];
  }, []);

  const handleConfirmMulti = useCallback(() => {
    if (multiProjects.length === 0 || selectedTaskIds.size === 0 || confirmAssigning) return;
    const taskIds = [...selectedTaskIds];
    const projectIds = multiProjects.map((p) => p.id.trim());
    void (async () => {
      setConfirmAssigning(true);
      const assignResult = await assignTasksToSelectedProjects({ projectIds, taskIds });
      setConfirmAssigning(false);
      if (!assignResult) return;
      for (const pid of projectIds) {
        u3129InvalidateFindTasksForProjectCache(
          u3129BuildFindTasksForProjectCacheKey({ projectId: pid, subprojectId: null }),
        );
      }
      const rows = u3129BuildHonorariumLinesForMultiProjects(
        multiProjects,
        taskIds,
        byProjectTasks,
        commonTaskList,
        activeCatalogList,
        { admIncludeSubProjectEnabled },
      );
      const cacheByProject = u3129BuildTasksForCacheByProjectId(
        byProjectTasks,
        pendingAssignTaskIds,
        commonTaskList,
        activeCatalogList,
      );
      onConfirm(rows, cacheByProject);
      resetDialog();
      onClose();
    })();
  }, [
    multiProjects,
    selectedTaskIds,
    confirmAssigning,
    assignTasksToSelectedProjects,
    byProjectTasks,
    commonTaskList,
    activeCatalogList,
    pendingAssignTaskIds,
    admIncludeSubProjectEnabled,
    onConfirm,
    resetDialog,
    onClose,
  ]);

  const handleConfirmSubproject = useCallback(() => {
    if (!selectedProject || selectedTaskIds.size === 0 || confirmAssigning) return;
    if (needsSubproject && !selectedSubproject?.id) return;
    const tasks = taskList.filter((tk) => selectedTaskIds.has(tk.id));
    if (tasks.length === 0) return;

    const pid = selectedProject.id.trim();
    const sidNorm = needsSubproject ? (selectedSubproject?.id ?? "").trim() : "";
    const scopeBody = {
      projectId: pid,
      subprojectId: needsSubproject ? sidNorm || null : null,
    };
    const taskIdsToAssign = [...pendingAssignTaskIds].filter((id) => selectedTaskIds.has(id));

    void (async () => {
      if (taskIdsToAssign.length > 0) {
        setConfirmAssigning(true);
        const assignResult = await assignTasksToProjectScope({
          ...scopeBody,
          taskIds: taskIdsToAssign,
        });
        setConfirmAssigning(false);
        if (!assignResult) return;
        u3129InvalidateFindTasksForProjectCache(u3129BuildFindTasksForProjectCacheKey(scopeBody));
      }
      const rows = buildHonorariumLinesFromProjectAndTasks(selectedProject, needsSubproject ? selectedSubproject : null, tasks, {
        admIncludeSubProjectEnabled,
      });
      onConfirm(rows, { [pid]: taskList });
      resetDialog();
      onClose();
    })();
  }, [
    selectedProject,
    selectedTaskIds,
    needsSubproject,
    selectedSubproject,
    taskList,
    pendingAssignTaskIds,
    confirmAssigning,
    assignTasksToProjectScope,
    admIncludeSubProjectEnabled,
    onConfirm,
    resetDialog,
    onClose,
  ]);

  const handleConfirm = selectionMode === "multi" ? handleConfirmMulti : handleConfirmSubproject;

  const toggleCatalogTaskSubproject = useCallback(
    (task: U3129TaskItem) => {
      const id = task.id;
      setCatalogTaskList((prev) => prev.filter((tk) => tk.id !== id));
      setTaskList((prev) => u3129SortTasksByDisplayLabel([...prev, task], i18n.language));
      setSelectedTaskIds((prev) => new Set(prev).add(id));
      setPendingAssignTaskIds((prev) => new Set(prev).add(id));
    },
    [i18n.language],
  );

  const toggleTaskSubproject = useCallback(
    (id: string) => {
      if (selectedTaskIds.has(id) && pendingAssignTaskIds.has(id)) {
        const task = taskList.find((tk) => tk.id === id);
        if (task) {
          setTaskList((list) => list.filter((tk) => tk.id !== id));
          setCatalogTaskList((cat) => u3129SortTasksByDisplayLabel([...cat, task], i18n.language));
          setPendingAssignTaskIds((pending) => {
            const p = new Set(pending);
            p.delete(id);
            return p;
          });
        }
      }
      setSelectedTaskIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    [selectedTaskIds, pendingAssignTaskIds, taskList, i18n.language],
  );

  const toggleCatalogTaskMulti = useCallback(
    (task: U3129TaskItem) => {
      const id = task.id;
      const inCommon = commonTaskList.some((tk) => tk.id === id);
      if (selectedTaskIds.has(id)) {
        setSelectedTaskIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        if (pendingAssignTaskIds.has(id)) {
          setPendingAssignTaskIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
          if (!inCommon) {
            setExtraLeftTaskIds((prev) => {
              const next = new Set(prev);
              next.delete(id);
              return next;
            });
          }
        }
        return;
      }
      setSelectedTaskIds((prev) => new Set(prev).add(id));
      setPendingAssignTaskIds((prev) => new Set(prev).add(id));
      if (!inCommon) {
        setExtraLeftTaskIds((prev) => new Set(prev).add(id));
      }
    },
    [commonTaskList, selectedTaskIds, pendingAssignTaskIds],
  );

  const toggleTaskMulti = useCallback(
    (id: string) => {
      const inCommon = commonTaskList.some((tk) => tk.id === id);
      if (selectedTaskIds.has(id) && pendingAssignTaskIds.has(id) && !inCommon) {
        setExtraLeftTaskIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        setPendingAssignTaskIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
      setSelectedTaskIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    [commonTaskList, selectedTaskIds, pendingAssignTaskIds],
  );

  const multiNewPairCount = useMemo(
    () => u3129CountNewHonorariumPairsMulti(multiProjects, selectedTaskIds, existingHonoraria),
    [multiProjects, selectedTaskIds, existingHonoraria],
  );

  const subprojectHasNewSelection = useMemo(() => {
    if (!selectedProject?.id?.trim() || selectedTaskIds.size === 0) return false;
    const pid = selectedProject.id.trim();
    const sidNorm = needsSubproject ? (selectedSubproject?.id ?? "").trim() : "";
    for (const id of selectedTaskIds) {
      if (!existingHonorariumKeys.has(u3129HonorariumLineProjectSubprojectTaskKeyFromParts(pid, sidNorm, id))) return true;
    }
    return false;
  }, [selectedProject, selectedTaskIds, needsSubproject, selectedSubproject, existingHonorariumKeys]);

  const tasksScopeLoading = tasksLoading || catalogLoading;

  const confirmDisabledMulti =
    multiProjects.length === 0 ||
    selectedTaskIds.size === 0 ||
    multiNewPairCount === 0 ||
    tasksScopeLoading ||
    confirmAssigning;

  const confirmDisabledSubproject =
    !selectedProject ||
    !subprojectHasNewSelection ||
    (needsSubproject && !selectedSubproject?.id) ||
    tasksScopeLoading ||
    confirmAssigning;

  const confirmDisabled = selectionMode === "multi" ? confirmDisabledMulti : confirmDisabledSubproject;

  const singleProjectStepTasks = selectionMode === "single" && !!selectedProject;

  const navigableTaskIndicesSubproject = useMemo(() => {
    if (!singleProjectStepTasks || !selectedProject?.id?.trim() || !canLoadTasksSubproject) return [] as number[];
    const pid = selectedProject.id.trim();
    const sidNorm = needsSubproject ? (selectedSubproject?.id ?? "").trim() : "";
    return taskList.reduce<number[]>((acc, tk, idx) => {
      if (!existingHonorariumKeys.has(u3129HonorariumLineProjectSubprojectTaskKeyFromParts(pid, sidNorm, tk.id))) {
        acc.push(idx);
      }
      return acc;
    }, []);
  }, [singleProjectStepTasks, taskList, selectedProject, needsSubproject, selectedSubproject, existingHonorariumKeys, canLoadTasksSubproject]);

  const navigableTaskIndicesMulti = useMemo(() => {
    if (selectionMode !== "multi" || multiProjects.length === 0) return [] as number[];
    return multiLeftTaskList.reduce<number[]>((acc, tk, idx) => {
      if (!u3129MultiTaskRowFullyOnSheet(tk.id, multiProjects, existingHonoraria)) {
        acc.push(idx);
      }
      return acc;
    }, []);
  }, [selectionMode, multiLeftTaskList, multiProjects, existingHonoraria]);

  const activeTaskList = selectionMode === "multi" ? multiLeftTaskList : taskList;
  const navigableTaskIndices = selectionMode === "multi" ? navigableTaskIndicesMulti : navigableTaskIndicesSubproject;

  const showMultiTaskSection = selectionMode === "multi" && multiProjects.length > 0;
  const showSingleTaskSection = singleProjectStepTasks;
  const showCatalogSection =
    (showMultiTaskSection || (showSingleTaskSection && canLoadTasksSubproject)) && !tasksScopeLoading;
  const catalogKeyboardList = selectionMode === "multi" ? activeCatalogList : catalogTaskList;
  const navigableCatalogIndices = useMemo(() => {
    if (!showCatalogSection || catalogKeyboardList.length === 0) {
      return [] as number[];
    }
    return catalogKeyboardList.map((_, idx) => idx);
  }, [showCatalogSection, catalogKeyboardList]);

  const navigableCommonTaskIds = useMemo(
    () =>
      navigableTaskIndices
        .map((listIdx) => activeTaskList[listIdx]?.id?.trim())
        .filter((id): id is string => !!id),
    [navigableTaskIndices, activeTaskList],
  );

  const navigableCatalogTaskIds = useMemo(
    () =>
      navigableCatalogIndices
        .map((listIdx) => catalogKeyboardList[listIdx]?.id?.trim())
        .filter((id): id is string => !!id),
    [navigableCatalogIndices, catalogKeyboardList],
  );

  const honorariumDialogTaskNavScopeKey = useMemo(() => {
    if (selectionMode === "multi") {
      return `multi:${multiProjects.map((p) => p.id.trim()).join("|")}`;
    }
    const pid = selectedProject?.id?.trim() ?? "";
    const sid = needsSubproject ? (selectedSubproject?.id?.trim() ?? "") : "";
    return `single:${pid}:${sid}`;
  }, [selectionMode, multiProjects, selectedProject?.id, needsSubproject, selectedSubproject?.id]);

  const commonTasksKeyboardReady =
    selectionMode === "multi" &&
    multiProjects.length > 0 &&
    !tasksScopeLoading &&
    multiLeftTaskList.length > 0 &&
    navigableCommonTaskIds.length > 0;

  const focusFirstCommonTaskFromProjectInput = useCallback(() => {
    if (!commonTasksKeyboardReady || navigableCommonTaskIds.length === 0) return;
    setFocusedTaskNav({ column: "common", taskId: navigableCommonTaskIds[0]! });
    const active = document.activeElement;
    if (active instanceof HTMLElement) {
      active.blur();
    }
  }, [commonTasksKeyboardReady, navigableCommonTaskIds]);

  useEffect(() => {
    setFocusedTaskNav(null);
  }, [honorariumDialogTaskNavScopeKey]);

  useEffect(() => {
    if (!focusedTaskNav) return;
    if (focusedTaskNav.column === "catalog") {
      const listIdx = catalogKeyboardList.findIndex((tk) => tk.id === focusedTaskNav.taskId);
      if (listIdx < 0) return;
      catalogTaskRowRefs.current[listIdx]?.scrollIntoView({ block: "nearest" });
      return;
    }
    const listIdx = activeTaskList.findIndex((tk) => tk.id === focusedTaskNav.taskId);
    if (listIdx < 0) return;
    taskRowRefs.current[listIdx]?.scrollIntoView({ block: "nearest" });
  }, [focusedTaskNav, activeTaskList, catalogKeyboardList]);

  useEffect(() => {
    if (!open) return;
    const tasksReady =
      selectionMode === "multi"
        ? multiProjects.length > 0 && !tasksScopeLoading && multiLeftTaskList.length > 0 && navigableTaskIndices.length > 0
        : singleProjectStepTasks &&
          canLoadTasksSubproject &&
          !tasksScopeLoading &&
          taskList.length > 0 &&
          navigableTaskIndices.length > 0;

    const blurHonorariumTaskNavCheckbox = () => {
      const active = document.activeElement;
      if (u3129IsHonorariumDialogTaskNavCheckbox(active)) {
        active.blur();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        handleCancel();
        return;
      }

      const active = document.activeElement;
      const tag = active?.tagName;
      const isTaskNavCheckbox = u3129IsHonorariumDialogTaskNavCheckbox(active);
      if (
        (tag === "INPUT" && !isTaskNavCheckbox) ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        active?.getAttribute("role") === "combobox"
      ) {
        return;
      }

      const nav = focusedTaskNavRef.current;
      const inTaskKeyboardNav = nav != null;

      const resolveNavIndex = (column: HonorariumDialogTaskFocusColumn, taskId: string): number => {
        const ids = column === "catalog" ? navigableCatalogTaskIds : navigableCommonTaskIds;
        const idx = ids.indexOf(taskId);
        return idx >= 0 ? idx : 0;
      };

      if (e.key === "ArrowRight" && inTaskKeyboardNav && nav.column === "common" && navigableCatalogTaskIds.length > 0) {
        e.preventDefault();
        const fromIdx = resolveNavIndex("common", nav.taskId);
        const targetIdx = Math.min(fromIdx, navigableCatalogTaskIds.length - 1);
        setFocusedTaskNav({ column: "catalog", taskId: navigableCatalogTaskIds[targetIdx]! });
        blurHonorariumTaskNavCheckbox();
        return;
      }
      if (e.key === "ArrowLeft" && inTaskKeyboardNav && nav.column === "catalog" && navigableCommonTaskIds.length > 0) {
        e.preventDefault();
        const fromIdx = resolveNavIndex("catalog", nav.taskId);
        const targetIdx = Math.min(fromIdx, navigableCommonTaskIds.length - 1);
        setFocusedTaskNav({ column: "common", taskId: navigableCommonTaskIds[targetIdx]! });
        blurHonorariumTaskNavCheckbox();
        return;
      }

      const verticalNavReady =
        inTaskKeyboardNav &&
        (nav.column === "catalog" ? navigableCatalogTaskIds.length > 0 : tasksReady || navigableCommonTaskIds.length > 0);

      if (e.key === "ArrowDown" && verticalNavReady) {
        e.preventDefault();
        const ids = nav.column === "catalog" ? navigableCatalogTaskIds : navigableCommonTaskIds;
        if (ids.length === 0) return;
        const idx = resolveNavIndex(nav.column, nav.taskId);
        const nextIdx = Math.min(idx + 1, ids.length - 1);
        setFocusedTaskNav({ column: nav.column, taskId: ids[nextIdx]! });
        blurHonorariumTaskNavCheckbox();
        return;
      }
      if (e.key === "ArrowUp" && verticalNavReady) {
        e.preventDefault();
        const ids = nav.column === "catalog" ? navigableCatalogTaskIds : navigableCommonTaskIds;
        if (ids.length === 0) return;
        const idx = resolveNavIndex(nav.column, nav.taskId);
        const nextIdx = idx <= 0 ? 0 : idx - 1;
        setFocusedTaskNav({ column: nav.column, taskId: ids[nextIdx]! });
        blurHonorariumTaskNavCheckbox();
        return;
      }
      if (e.key === " " && inTaskKeyboardNav) {
        e.preventDefault();
        if (nav.column === "catalog") {
          const task = catalogKeyboardList.find((tk) => tk.id === nav.taskId);
          if (task) {
            if (selectionMode === "multi") toggleCatalogTaskMulti(task);
            else toggleCatalogTaskSubproject(task);
          }
        } else {
          if (selectionMode === "multi") toggleTaskMulti(nav.taskId);
          else toggleTaskSubproject(nav.taskId);
        }
        return;
      }
      if (e.key === "Enter" && !confirmDisabled && !isTaskNavCheckbox) {
        e.preventDefault();
        handleConfirm();
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [
    open,
    selectionMode,
    singleProjectStepTasks,
    handleCancel,
    navigableTaskIndices,
    navigableCommonTaskIds,
    navigableCatalogTaskIds,
    multiProjects.length,
    selectedProject,
    canLoadTasksSubproject,
    tasksScopeLoading,
    multiLeftTaskList,
    taskList,
    activeTaskList,
    toggleTaskMulti,
    toggleTaskSubproject,
    toggleCatalogTaskMulti,
    toggleCatalogTaskSubproject,
    navigableCatalogIndices,
    catalogKeyboardList,
    confirmDisabled,
    handleConfirm,
  ]);

  const syncTaskNavFromPointer = useCallback((column: HonorariumDialogTaskFocusColumn, taskId: string) => {
    setFocusedTaskNav({ column, taskId });
  }, []);

  const renderTaskLists = (mode: "multi" | "single") => {
    const leftList = mode === "multi" ? multiLeftTaskList : taskList;
    const leftHeadingKey =
      mode === "multi" ? "dlgAddHonorariumLinesCommonTasksHeading" : "dlgAddHonorariumLinesTasksHeading";
    const leftEmptyKey =
      mode === "multi" ? "dlgAddHonorariumLinesNoCommonTasks" : "dlgAddHonorariumLinesNoTasks";
    const leftHintKey =
      mode === "multi" ? "dlgAddHonorariumLinesCommonTasksEmptyHint" : "dlgAddHonorariumLinesTasksEmptyHint";
    const catalogHeadingKey =
      mode === "multi" ? "dlgAddHonorariumLinesAllTasksHeading" : "dlgAddHonorariumLinesCatalogHeading";
    const catalogEmptyKey =
      mode === "multi" ? "dlgAddHonorariumLinesAllTasksEmpty" : "dlgAddHonorariumLinesCatalogEmpty";
    const catalogList = mode === "multi" ? activeCatalogList : catalogTaskList;
    const toggleLeft = mode === "multi" ? toggleTaskMulti : toggleTaskSubproject;
    const toggleCatalog = mode === "multi" ? toggleCatalogTaskMulti : toggleCatalogTaskSubproject;

    const resolveFullyOnSheet = (taskId: string) => {
      if (mode === "multi") {
        return u3129MultiTaskRowFullyOnSheet(taskId, multiProjects, existingHonoraria);
      }
      const pid = selectedProject!.id.trim();
      const sidNorm = needsSubproject ? (selectedSubproject?.id ?? "").trim() : "";
      return existingHonorariumKeys.has(u3129HonorariumLineProjectSubprojectTaskKeyFromParts(pid, sidNorm, taskId));
    };

    const showTaskKeyboardHints = navigableTaskIndices.length > 0 || navigableCatalogIndices.length > 0;
    const taskKeyboardHintsFooter = showTaskKeyboardHints ? (
      <div className="flex shrink-0 flex-wrap gap-x-4 gap-y-0.5 border-t border-gray-100 px-3 py-1.5 text-xs text-gray-500">
        <span className="inline-flex items-center gap-1.5">
          <U2000KeyboardKeyHint>↑</U2000KeyboardKeyHint>
          <U2000KeyboardKeyHint>↓</U2000KeyboardKeyHint>
          <span>{t("keyboardHintTasksNavigate", { ns: NS, defaultValue: "Parcourir les tâches" })}</span>
        </span>
        {showCatalogSection && navigableCatalogIndices.length > 0 ? (
          <span className="inline-flex items-center gap-1.5">
            <U2000KeyboardKeyHint>←</U2000KeyboardKeyHint>
            <U2000KeyboardKeyHint>→</U2000KeyboardKeyHint>
            <span>
              {t("keyboardHintTasksSwitchColumn", {
                ns: NS,
                defaultValue: "Changer de liste",
              })}
            </span>
          </span>
        ) : null}
        <span className="inline-flex items-center gap-1.5">
          <U2000KeyboardKeyHint>{t("keyboardHintSpaceKey", { ns: "common", defaultValue: "Espace" })}</U2000KeyboardKeyHint>
          <span>{t("keyboardHintTasksToggle", { ns: NS, defaultValue: "Cocher / décocher" })}</span>
        </span>
        {!confirmDisabled ? (
          <span className="inline-flex items-center gap-1.5">
            <U2000KeyboardKeyHint>{t("keyboardHintEnterKey", { ns: "common", defaultValue: "Entrée" })}</U2000KeyboardKeyHint>
            <span>{t("keyboardHintTasksConfirm", { ns: NS, defaultValue: "Ajouter les lignes" })}</span>
          </span>
        ) : null}
        <span className="inline-flex items-center gap-1.5">
          <U2000KeyboardKeyHint>{t("keyboardHintEscapeKey", { ns: "common", defaultValue: "Échap" })}</U2000KeyboardKeyHint>
          <span>{t("keyboardHintCancelDialog", { ns: NS, defaultValue: "Annuler" })}</span>
        </span>
      </div>
    ) : null;

    return (
      <div className="flex min-h-0 flex-1 flex-row gap-4 overflow-hidden">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-1.5 border-r border-gray-200 pr-4">
          <span className="shrink-0 text-sm font-medium text-gray-800">
            {t(leftHeadingKey, {
              ns: NS,
              defaultValue: mode === "multi" ? "Tâches communes" : "Tâches",
            })}
          </span>
          {mode === "single" && canLoadTasksSubproject && leftList.length === 0 ? (
            <p className="shrink-0 text-xs text-amber-700">
              {t(leftEmptyKey, { ns: NS, defaultValue: "Aucune tâche pour ce projet." })}
            </p>
          ) : null}
          {mode === "multi" && leftList.length === 0 && !tasksScopeLoading ? (
            <p className="shrink-0 text-xs text-amber-700">
              {t(leftEmptyKey, { ns: NS, defaultValue: "Aucune tâche commune à tous les projets sélectionnés." })}
            </p>
          ) : null}
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border border-gray-200 bg-surface">
            {leftList.length === 0 ? (
              <div className="flex min-h-[160px] flex-1 items-center justify-center px-3 py-2">
                <p className="text-center text-xs text-gray-500">
                  {t(leftHintKey, {
                    ns: NS,
                    defaultValue:
                      mode === "multi"
                        ? "Cochez des tâches communes ou choisissez-en à droite."
                        : "Cochez des tâches à droite pour les ajouter au projet.",
                  })}
                </p>
              </div>
            ) : (
              <>
                <ul className="m-0 min-h-0 flex-1 list-none divide-y divide-gray-100 overflow-y-auto p-0">
                  {leftList.map((tk, listIdx) => {
                    const alreadyOnSheet = resolveFullyOnSheet(tk.id);
                    const isFocused =
                      !alreadyOnSheet && focusedTaskNav?.column === "common" && focusedTaskNav.taskId === tk.id;
                    return (
                      <li
                        key={tk.id}
                        ref={(el) => {
                          taskRowRefs.current[listIdx] = el;
                        }}
                        onPointerDown={() => {
                          if (!alreadyOnSheet) {
                            syncTaskNavFromPointer("common", tk.id);
                          }
                        }}
                        className={`flex items-start gap-3 px-3 py-2 ${
                          alreadyOnSheet
                            ? "bg-gray-50/60"
                            : isFocused
                              ? "bg-blue-50 text-blue-900"
                              : "hover:bg-gray-50/80"
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4 shrink-0 disabled:cursor-not-allowed"
                          disabled={alreadyOnSheet}
                          checked={!alreadyOnSheet && selectedTaskIds.has(tk.id)}
                          onChange={() => toggleLeft(tk.id)}
                          onFocus={() => {
                            if (!alreadyOnSheet) {
                              syncTaskNavFromPointer("common", tk.id);
                            }
                          }}
                          data-u3129-hon-task-nav="common"
                          id={`u3129-add-hon-task-${tk.id}`}
                        />
                        {alreadyOnSheet ? (
                          <div className="min-w-0 flex-1 text-sm text-gray-500">
                            <span>{formatU3129TaskLabel(tk, i18n.language)}</span>
                            <span className="ml-2 text-xs">
                              {t("dlgAddHonorariumLinesTaskAlreadyOnSheet", { ns: NS, defaultValue: "(déjà sur la feuille)" })}
                            </span>
                          </div>
                        ) : (
                          <label htmlFor={`u3129-add-hon-task-${tk.id}`} className="min-w-0 flex-1 cursor-pointer text-sm text-gray-800">
                            {formatU3129TaskLabel(tk, i18n.language)}
                          </label>
                        )}
                      </li>
                    );
                  })}
                </ul>
                {taskKeyboardHintsFooter}
              </>
            )}
          </div>
        </div>

        {showCatalogSection ? (
          <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-1.5">
            <span className="shrink-0 text-sm font-medium text-gray-800">
              {t(catalogHeadingKey, {
                ns: NS,
                defaultValue: mode === "multi" ? "Toutes les tâches actives" : "Tâches disponibles à ajouter au projet",
              })}
            </span>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border border-gray-200 bg-surface">
              {catalogList.length === 0 ? (
                <div className="flex min-h-[160px] flex-1 items-center justify-center px-3 py-2">
                  <p className="text-center text-xs text-gray-500">
                    {t(catalogEmptyKey, {
                      ns: NS,
                      defaultValue: mode === "multi" ? "Aucune tâche active." : "Aucune autre tâche active à ajouter.",
                    })}
                  </p>
                </div>
              ) : (
                <>
                  <ul className="m-0 min-h-0 flex-1 list-none divide-y divide-gray-100 overflow-y-auto p-0">
                    {catalogList.map((tk, listIdx) => {
                      const isCatalogFocused =
                        focusedTaskNav?.column === "catalog" && focusedTaskNav.taskId === tk.id;
                      return (
                        <li
                          key={tk.id}
                          ref={(el) => {
                            catalogTaskRowRefs.current[listIdx] = el;
                          }}
                          onPointerDown={() => syncTaskNavFromPointer("catalog", tk.id)}
                          className={`flex items-start gap-3 px-3 py-2 ${
                            isCatalogFocused ? "bg-blue-50 text-blue-900" : "hover:bg-gray-50/80"
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="mt-1 h-4 w-4 shrink-0"
                            checked={selectedTaskIds.has(tk.id)}
                            onChange={() => toggleCatalog(tk)}
                            onFocus={() => syncTaskNavFromPointer("catalog", tk.id)}
                            data-u3129-hon-task-nav="catalog"
                            id={`u3129-add-hon-catalog-${tk.id}`}
                          />
                          <label htmlFor={`u3129-add-hon-catalog-${tk.id}`} className="min-w-0 flex-1 cursor-pointer text-sm text-gray-800">
                            {formatU3129TaskLabel(tk, i18n.language)}
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                  {taskKeyboardHintsFooter}
                </>
              )}
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const renderMultiTasksArea = () => {
    if (showMultiTaskSection && tasksScopeLoading) {
      return (
        <div className="flex min-h-0 flex-1 items-center justify-center rounded-md border border-gray-200 bg-surface px-3 py-4">
          <p className="text-sm text-gray-500">{t("msgHonorariumTasksLoading", { ns: NS })}</p>
        </div>
      );
    }
    if (showMultiTaskSection) {
      return renderTaskLists("multi");
    }
    return (
      <div className="flex min-h-0 flex-1 flex-row gap-4 overflow-hidden">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-1.5 border-r border-gray-200 pr-4">
          <span className="shrink-0 text-sm font-medium text-gray-800">
            {t("dlgAddHonorariumLinesCommonTasksHeading", { ns: NS, defaultValue: "Tâches communes" })}
          </span>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border border-gray-200 bg-surface">
            <div className="flex min-h-0 flex-1 items-center justify-center px-3 py-2">
              <p className="text-center text-xs text-gray-500">
                {t("dlgAddHonorariumLinesTasksAwaitProjects", {
                  ns: NS,
                  defaultValue: "Ajoutez au moins un projet pour afficher les tâches.",
                })}
              </p>
            </div>
          </div>
        </div>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-1.5">
          <span className="shrink-0 text-sm font-medium text-gray-800">
            {t("dlgAddHonorariumLinesAllTasksHeading", { ns: NS, defaultValue: "Toutes les tâches actives" })}
          </span>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border border-gray-200 bg-surface">
            <div className="flex min-h-0 flex-1 items-center justify-center px-3 py-2">
              <p className="text-center text-xs text-gray-500">
                {t("dlgAddHonorariumLinesAllTasksEmpty", { ns: NS, defaultValue: "Aucune tâche active." })}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <U2000BasicDialog
      isOpen={open}
      onClose={() => {
        handleCancel();
      }}
      title={t("dlgAddHonorariumLinesTitle", { ns: NS, defaultValue: "Ajouter des lignes honoraires" })}
      width="w-[50vw]"
      contentOverflowVisible
    >
      <div className="flex min-h-0 flex-col gap-4 px-6 py-4">
        <div className={`flex min-h-0 flex-col overflow-hidden ${U3129_ADD_HON_DIALOG_BODY_HEIGHT_CLASS}`}>
          {!singleProjectStepTasks ? (
            <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
              <div className="shrink-0">
                <U2000TagAutocomplete<U3129FindProjectItem>
                  value={projectTags}
                  onChange={handleProjectTagsChange}
                  onSearch={chipProjectSearch}
                  renderSuggestion={(item) => u3129FindProjectChipTitle(item)}
                  itemToTag={(item) => ({
                    value: item.id,
                    label: u3129FindProjectChipLabel(item),
                    meta: item,
                  })}
                  getTagKey={(tag) => tag.value}
                  getTagLabel={(tag) => tag.label ?? tag.value}
                  getTagTitle={(tag) => {
                    const project = tag.meta as U3129FindProjectItem | undefined;
                    return project ? u3129FindProjectChipTitle(project) : tag.label ?? tag.value;
                  }}
                  searchCriterionField="projetNumber"
                  maxTags={U3129_HONORARIUM_MULTI_PROJECT_MAX}
                  allowFreeText={false}
                  placeholder={t("dlgAddHonorariumLinesProjectsPlaceholder", {
                    ns: NS,
                    defaultValue: "Numéro ou description de projet…",
                  })}
                  removeTagAriaLabel={(tag) => {
                    const project = tag.meta as U3129FindProjectItem | undefined;
                    const label = project ? u3129FindProjectChipTitle(project) : (tag.label ?? tag.value);
                    return t("dlgAddHonorariumLinesRemoveProjectAria", {
                      ns: NS,
                      defaultValue: "Retirer le projet {{label}}",
                      label,
                    });
                  }}
                  onMaxTagsError={() => {
                    toast.warning(
                      t("dlgAddHonorariumLinesMaxProjects", {
                        ns: NS,
                        defaultValue: "Maximum {{max}} projets sur la ligne.",
                        max: U3129_HONORARIUM_MULTI_PROJECT_MAX,
                      }),
                    );
                  }}
                  onDuplicateError={() => {
                    toast.info(
                      t("dlgAddHonorariumLinesProjectAlreadySelected", {
                        ns: NS,
                        defaultValue: "Ce projet est déjà sur la ligne.",
                      }),
                    );
                  }}
                  footerText={t("dlgAddHonorariumLinesProjectsFieldHint", {
                    ns: NS,
                    max: U3129_HONORARIUM_MULTI_PROJECT_MAX,
                    defaultValue:
                      "Ajoutez 1 à {{max}} projets : saisissez un numéro ou une description, puis choisissez dans la liste.",
                  })}
                  onInputFocus={() => setFocusedTaskNav(null)}
                  onArrowDownFromInput={focusFirstCommonTaskFromProjectInput}
                />
              </div>
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{renderMultiTasksArea()}</div>
            </div>
          ) : null}

          {singleProjectStepTasks ? (
            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">
              <div className="shrink-0 rounded-lg border border-gray-200 bg-gray-50/80 p-3 text-sm">
                <div className="font-medium text-gray-900">
                  {selectedProject?.projetNumber ?? selectedProject?.id} — {selectedProject?.projetName ?? ""}
                </div>
                {(selectedProject?.customerName ?? selectedProject?.customerCode) ? (
                  <div className="mt-1 text-gray-600">
                    {[selectedProject?.customerName, selectedProject?.customerCode ? `(${selectedProject.customerCode})` : ""]
                      .filter(Boolean)
                      .join(" ")}
                  </div>
                ) : null}
                <button
                  type="button"
                  className="mt-2 text-sm font-medium text-blue-700 hover:text-blue-900"
                  onClick={() => {
                    setSelectionMode("multi");
                    setSelectedProject(null);
                    subprojectFormHandlers.setData({ subproject: null });
                    setTaskList([]);
                    setCatalogTaskList([]);
                    clearTaskPickState();
                  }}
                >
                  {t("dlgAddHonorariumLinesChangeProject", { ns: NS, defaultValue: "Changer de projet" })}
                </button>
              </div>

              {needsSubproject ? (
                <div className="flex min-w-0 shrink-0 flex-col gap-1.5">
                  <label className="text-sm font-medium text-gray-700">{t("lblSubProject", { ns: NS })}</label>
                  <div className="text-left [&_input]:text-left min-w-0">
                    <U2000AutoCompleteInput
                      pageSize={15}
                      formStateHandlers={subprojectFormHandlers as never}
                      fieldName="subproject"
                      mainFieldName="subProjetNumber"
                      label=""
                      onSearch={onSearchSubproject}
                      displayFn={(item) => {
                        const sp = item as U3129FindSubprojectItem;
                        return sp ? sp.subProjetNumber?.trim() || sp.id : "";
                      }}
                      overrideExtractDataFn={(item) => extractSubproject(item as U3129FindSubprojectItem | null)}
                      basicResultHeight={240}
                      resultLabels={[]}
                      resultFields={["subProjetNumber", "subProjetName"]}
                      minCarLaunchSearch={0}
                    />
                  </div>
                </div>
              ) : null}

              <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
                {!canLoadTasksSubproject && needsSubproject ? (
                  <div className="flex min-h-[200px] flex-1 items-center justify-center rounded-md border border-gray-200 bg-surface px-3 py-4">
                    <p className="text-center text-xs text-gray-500">
                      {t("dlgAddHonorariumLinesSubprojectRequired", { ns: NS, defaultValue: "Sélectionnez un sous-projet pour charger les tâches." })}
                    </p>
                  </div>
                ) : tasksScopeLoading ? (
                  <div className="flex min-h-[200px] flex-1 items-center justify-center rounded-md border border-gray-200 bg-surface px-3 py-4">
                    <p className="text-sm text-gray-500">{t("msgHonorariumTasksLoading", { ns: NS })}</p>
                  </div>
                ) : (
                  renderTaskLists("single")
                )}
              </div>
            </div>
          ) : null}
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t border-gray-200 pt-4">
          <button
            type="button"
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            onClick={handleCancel}
          >
            {t("btnCancel", { ns: "common", defaultValue: "Annuler" })}
          </button>
          <button
            type="button"
            disabled={confirmDisabled}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-gray-400 disabled:text-gray-200"
            onClick={handleConfirm}
          >
            {t("dlgAddHonorariumLinesConfirm", { ns: NS, defaultValue: "Ajouter les lignes" })}
          </button>
        </div>
      </div>
    </U2000BasicDialog>
  );
};

export default U3129AddHonorariumLinesDialog;
